import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface WebsiteAssessment {
  id: string;
  company_id: string;
  location_id: string | null;
  website_url: string;
  url_works: boolean;
  loads_successfully: boolean;
  has_phone_number: boolean;
  has_email: boolean;
  has_address: boolean;
  has_ssl: boolean;
  has_social_links: boolean;
  has_online_booking: boolean;
  has_testimonials: boolean;
  has_photo_gallery: boolean;
  has_blog: boolean;
  mobile_friendly: boolean;
  has_meta_title: boolean;
  has_meta_description: boolean;
  has_meta_keywords: boolean;
  load_time_ms: number | null;
  assessment_date: string;
  status_code: number | null;
  error_message: string | null;
  phone_numbers_found: string[] | null;
  emails_found: string[] | null;
  social_links_found: string[] | null;
  booking_links_found: string[] | null;
  performance_score: number | null;
  accessibility_score: number | null;
  best_practices_score: number | null;
  seo_score: number | null;
  seo_rankings: Array<{
    keyword: string;
    position: number | null;
    checked_at: string;
    total_results: string | null;
  }> | null;
  ai_summary: string | null;
}

export function useWebsiteAssessment() {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: ['website-assessment', companyId],
    queryFn: async () => {
      if (!companyId) return null;

      try {
        const { data, error } = await (supabase as any)
          .from('website_assessments')
          .select('*')
          .eq('company_id', companyId)
          .order('assessment_date', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          // If table doesn't exist (migration not applied), return null gracefully
          if (error.code === 'PGRST116' || error.message?.includes('does not exist')) {
            console.warn('Website assessments table not found. Migrations may not be applied.');
            return null;
          }
          console.error('Error fetching website assessment:', error);
          throw error;
        }

        return data as WebsiteAssessment | null;
      } catch (error: any) {
        // Handle table not found gracefully
        if (error?.code === 'PGRST116' || error?.message?.includes('does not exist')) {
          console.warn('Website assessments table not found. Migrations may not be applied.');
          return null;
        }
        throw error;
      }
    },
    enabled: !!companyId,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });
}

export function useTriggerWebsiteAssessment() {
  const queryClient = useQueryClient();
  const { data: companyId } = useCurrentCompany();

  return useMutation({
    mutationFn: async (websiteUrl: string) => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }

      // Validate URL format before calling Edge Function
      let normalizedUrl = websiteUrl.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }

      try {
        new URL(normalizedUrl);
      } catch (urlError) {
        throw new Error(`Invalid URL format: "${websiteUrl}" is not a valid URL. Please check the format and try again.`);
      }

      // Call Edge Function to assess website
      const { data, error } = await supabase.functions.invoke('assess-website', {
        body: {
          website_url: normalizedUrl,
          company_id: companyId,
        },
      });

      if (error) {
        // Provide more user-friendly error messages
        if (error.message?.includes('timeout') || error.message?.includes('timed out')) {
          throw new Error('Website assessment timed out. The website may be slow, unreachable, or blocking automated requests. Please verify the URL is correct and try again.');
        } else if (error.message?.includes('Invalid URL')) {
          throw new Error(`Invalid URL: ${error.message}`);
        } else {
          throw new Error(error.message || 'Failed to assess website. Please check the URL and try again.');
        }
      }

      if (data?.error) {
        throw new Error(data.error + (data.details ? ` ${data.details}` : ''));
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-assessment', companyId] });
    },
  });
}
